'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useNotification } from '../../ui/components';
import { UNAUTHORIZED_REASON_COPY } from '../../utilities/errors/unauthorizedReason';
import { invalidateSentrySessionCache } from '../cache';
import { isAuthPage, signOut } from '../session';
import { onSessionExpired } from './sessionExpiryCoordinator';

/** Delay before redirecting so the user can read the sign-out message. */
const SIGN_OUT_DELAY_MS = 2500;
export type SessionExpiryListenerProps = {
  /** Where to send the user after sign-out. Defaults to the app origin. */
  redirectTo?: string;
};

/**
 * Headless component that listens for session-expiry events (raised by the
 * global query error handler) and signs the user out with a clear message,
 * instead of leaving them on an "Unauthorized" error screen.
 *
 * Render once near the app root, inside the auth `SessionProvider`.
 */
export function SessionExpiryListener(t0) {
  const $ = _c(4);
  const {
    redirectTo
  } = t0;
  const {
    enqueueSnackbar
  } = useNotification();
  const hasTriggeredRef = React.useRef(false);
  let t1;
  let t2;
  if ($[0] !== enqueueSnackbar || $[1] !== redirectTo) {
    t1 = () => {
      const unsubscribe = onSessionExpired(reason => {
        if (hasTriggeredRef.current) {
          return;
        }
        if (isAuthPage(window.location.pathname)) {
          return;
        }
        hasTriggeredRef.current = true;
        enqueueSnackbar(`${UNAUTHORIZED_REASON_COPY[reason].message} Signing you out…`, {
          variant: "warning",
          toastId: "session-expired",
          autoClose: SIGN_OUT_DELAY_MS
        });
        invalidateSentrySessionCache();
        const target = redirectTo ?? (typeof window !== "undefined" ? window.location.origin : "/");
        window.setTimeout(() => {
          signOut({
            redirectTo: target
          });
        }, SIGN_OUT_DELAY_MS);
      });
      return unsubscribe;
    };
    t2 = [enqueueSnackbar, redirectTo];
    $[0] = enqueueSnackbar;
    $[1] = redirectTo;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  React.useEffect(t1, t2);
  return null;
}